import * as React from 'react';
import Axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import moment, { Moment } from 'moment';
import { Client } from 'filestack-js/build/main/lib/client';
import {
  PickerOptions,
  PickerResponse,
  PickerInstance,
} from 'filestack-js/build/main/lib/picker';
import Swal from 'sweetalert2';
import { OfferController, UserController } from 'api';
import styled from 'styled-components';
import {
  getPhoneInputMask,
  isAssumeOrgType,
  isAuthForRole,
  replaceTagsWithMarkup,
  formatPhoneNumber,
  isFullAccessUser,
  isValidUrl,
  isManualCoopUserWithAccess,
} from 'utils';
import { CachedOrg } from '@bb/automaton';
import { OfferSettingsJsonType } from '@bb/domain/dist/types';
import errorReporter from '@app/errorReporter';
import { ROLES, TrrebApiReason, UserRoles } from '@bb/users';
import { OfferDisabledRegistration } from '@app/DashboardComponents/OfferViewComponents';
import { AuthContextUser } from 'contexts';
import { Modal } from 'components';
import { format } from 'date-fns';
import OfferRegistrationModal, { Coop } from '../OfferRegistrationModal';
import { AgentType } from '../OfferRegistrationModal/types';
import PanelHeader from '../../react-common/PanelHeader';
import PanelBody from '../../react-common/PanelBody';
import Loading from '../../react-common/Loading';
import AgentRosterInput, {
  type AgentUserResult,
} from '../../react-common/AgentRosterInput';
import ActionTabs from '../../react-common/ActionTabs';
import { DatePicker, TimePicker } from '../../react-common/Pickers';
import {
  UNCLAIMED_LISTING_SWAL,
  UNVERIFIED_AGENT_MODAL,
  UNVERIFIED_ADMIN_MODAL,
} from '../../constants';
import AgentOfferEdit from './AgentOfferEdit';
import {
  CoopInputKeys,
  CoopOrgInputKeys,
  ListingWithUserPopulated,
  ManualCoopErrors,
  NotificationOptions,
  OfferDetails,
  OfferEditProps,
  OfferForm,
  SearchCoopErrors,
} from './types';

moment.locale('en');

declare const fileStackClient: Client;

type Errors = SearchCoopErrors | ManualCoopErrors;

const coopInputErrorMap: Record<CoopInputKeys, Errors> = {
  email: 'coopEmail',
  phone: 'coopPhone',
  name: 'coopName',
};
const coopOrgInputErrorMap: Record<CoopOrgInputKeys, Errors> = {
  phone: 'coopOrgPhone',
  name: 'coopOrgName',
};

const SEARCH_COOP_ERRORS: SearchCoopErrors[] = [
  'coopEmail',
  'coopPhone',
  'irrevocable',
  'pastDate',
  'beforeCsDate',
  'invalidDate',
  'invalidTime',
];
const MANUAL_COOP_ERRORS: ManualCoopErrors[] = [
  'coopEmail',
  'coopPhone',
  'coopName',
  'coopOrgName',
  'coopOrgPhone',
  'irrevocable',
  'pastDate',
  'beforeCsDate',
  'invalidDate',
  'invalidTime',
];

interface OfferEditState {
  loading: boolean;
  offerRegistrationModalVisible: boolean;
  offer?: OfferForm;
  errors: Record<Errors, boolean>;
  attachmentForm801?: { url?: string; filename?: string };
  userType?: AgentType;
  listing?: ListingWithUserPopulated & { isUnclaimed: boolean };
  coopSelected: boolean;
  timePickerValue?: string;
  datePickerValue?: string;
  offerDetails?: OfferDetails;
  hasListingAccess?: boolean;
  canRegisterOffer?: boolean;
  notificationOptions: NotificationOptions | null;
}

class OfferEdit extends React.Component<OfferEditProps, OfferEditState> {
  private phoneRef = React.createRef<HTMLInputElement>();
  private companyPhoneRef = React.createRef<HTMLInputElement>();

  constructor(props: OfferEditProps) {
    super(props);
    this.state = {
      errors: {
        coopName: false,
        coopPhone: false,
        coopEmail: false,
        coopOrgName: false,
        coopOrgPhone: false,
        irrevocable: false,
        pastDate: false,
        beforeCsDate: false,
        invalidDate: false,
        invalidTime: false,
      },
      offerRegistrationModalVisible: false,
      offer: {
        coop: { name: '', email: '', phone: '', org: { name: '', phone: '' } },
      },
      loading: true,
      userType: 'agent',
      listing: undefined,
      coopSelected: false,
      offerDetails: undefined,
      hasListingAccess: false,
      canRegisterOffer: true,
      notificationOptions: null,
    };
  }

  public componentDidMount() {
    this.fetchData();
  }

  public componentDidUpdate() {
    if (this.phoneRef.current && !this.phoneRef.current.inputmask) {
      getPhoneInputMask().mask(this.phoneRef.current);
    }
    if (
      this.companyPhoneRef.current &&
      !this.companyPhoneRef.current.inputmask
    ) {
      getPhoneInputMask().mask(this.companyPhoneRef.current);
    }
  }

  public componentWillUnmount() {
    if (this.onHistoryClosePicker) {
      this.onHistoryClosePicker();
    }
  }

  private onHistoryClosePicker: (() => void) | null = null;

  public fetchData = async () => {
    try {
      const { isNew, $rootScope } = this.props;
      const loadedState: {
        userType: AgentType;
        listing: ListingWithUserPopulated & { isUnclaimed: boolean };
        offer?: OfferForm;
      } = await this.fetchListing();
      if (!isNew) loadedState.offer = await this.fetchOffer();
      this.setState(loadedState, () => {
        if (isNew) this.selectAgentLookup('search');
      });

      // Fetching Offer Details
      const offerDetails = await this.fetchOfferDetails();
      this.setState({ offerDetails });

      // Fetching Notification Options
      if (ROLES[$rootScope.auth.role] < ROLES.TEAM_ADMIN) {
        await this.fetchNotificationOptions();
      }
      // checking if user has listing access
      if (
        isFullAccessUser($rootScope.auth) ||
        isAuthForRole({
          auth: { ...$rootScope.auth, roles: ROLES },
          org: loadedState.listing.org.id,
          locations: loadedState.listing.locations,
          teams: [loadedState.listing.team],
          reps: loadedState.listing.reps,
        })
      ) {
        this.setState({ hasListingAccess: true });
      } else {
        const {
          listing: { org: listingOrg },
        } = this.state;
        const isSameOrAnsweringOrg =
          ($rootScope.auth.org.id &&
            $rootScope.auth.org.id === listingOrg._id) ||
          isAssumeOrgType($rootScope.auth.org.type);

        let canRegisterOffer;
        if (!isSameOrAnsweringOrg) {
          const currentUserOrgMlsType = $rootScope.auth.org.extendedMlsTypes;
          const isSameMlsType =
            !!currentUserOrgMlsType &&
            listingOrg.mlsTypes.some(mlsType =>
              currentUserOrgMlsType.includes(mlsType),
            );

          canRegisterOffer =
            isSameMlsType ||
            isManualCoopUserWithAccess(
              $rootScope.auth,
              listingOrg as unknown as CachedOrg,
            );
        } else {
          canRegisterOffer = true;
        }
        this.setState({ canRegisterOffer });
      }
    } catch (err) {
      errorReporter.error(err);
    } finally {
      this.setState({ loading: false });
    }
  };

  public fetchListing = async () => {
    const {
      $routeParams: { listingId },
      $rootScope,
      ToastService,
    } = this.props;
    try {
      const { auth } = $rootScope;
      if (!listingId || !auth) return;

      const { data: listing } = await Axios.get<
        ListingWithUserPopulated & { isUnclaimed: boolean }
      >(`/listing/${listingId}`);

      const isListingAgent = listing.reps.some(rep => rep.id === auth.id);
      const { isAdmin } = auth;
      const userType: AgentType =
        !isListingAgent && isAdmin ? 'admin' : 'agent';

      /**
       * If a user is unverified and is NOT an admin for this listing and is NOT the listing agent for this listing, prevent them from booking appointments
       */

      const denyAccess = !isFullAccessUser(auth) && auth.isUnverified;

      if (denyAccess) {
        const isAgent = auth.role === UserRoles.Agent;
        const redirectAccess = isAgent && !isListingAgent;

        Modal.warning({
          ...(isAgent ? UNVERIFIED_AGENT_MODAL : UNVERIFIED_ADMIN_MODAL),
          onOk: redirectAccess
            ? () => {
                window.location.href = `#/listing/${listing.id}/view`;
              }
            : undefined,
        });

        if (redirectAccess) return;
      }

      if (!isFullAccessUser(auth) && listing?.isUnclaimed) {
        swal(UNCLAIMED_LISTING_SWAL, () => {
          window.location.href = `/#/listing/${listing.id}/view`;
        });
      }

      return { userType, listing };
    } catch (err) {
      errorReporter.error(`Error fetching listing data: ${err}`);
      $rootScope.$evalAsync(() => {
        ToastService.add({
          type: 'danger',
          message: `Error fetching listing data: ${
            err?.response?.data ? err.response.data.message : err
          }`,
        });
      });
    }
  };

  public fetchOffer = async () => {
    const {
      $routeParams: { listingId, offerId },
      $rootScope,
      ToastService,
    } = this.props;
    try {
      const { data: offer } = await Axios.get<OfferForm>(
        `/listing/${listingId}/offer/${offerId}?getInactive=true`,
      );

      if (offer.attachmentForm801) {
        const attachmentForm801 = {
          url: offer.attachmentForm801.url,
          filename: offer.attachmentForm801.filename,
        };
        this.setState({ attachmentForm801 });
      }
      if (offer.irrevocable) {
        offer.irrevocable = new Date(offer.irrevocable);
      }

      return offer;
    } catch (err) {
      errorReporter.error(`Error fetching offer data: ${err}`);
      $rootScope.$evalAsync(() => {
        ToastService.add({
          type: 'danger',
          message: `Error fetching offer data: ${
            err?.response?.data ? err.response.data.message : err
          }`,
        });
      });
    }
  };

  public redirectToOffers = () => {
    const {
      $routeParams: { listingId },
    } = this.props;
    window.location.replace(`#/listing/${listingId}/offer`);
  };

  public selectAgentLookup = (agentLookup: string) => {
    const { listing, offer } = this.state;
    const defaultOfferDate = moment(
      listing?.status === 'Coming Soon' ? listing.coming_soon_date! : undefined,
    )
      .endOf('day')
      .toDate();
    this.setState({
      offer: {
        agentLookup,
        coop: { name: '', email: '', phone: '', org: { name: '', phone: '' } },
        ...(!offer?.irrevocable && { irrevocable: defaultOfferDate }),
      },
      coopSelected: false,
      datePickerValue: format(defaultOfferDate, 'yyyy-MM-dd'),
      timePickerValue: format(defaultOfferDate, 'hh:mm b'),
    });
  };

  public onHistoryChange = (picker: PickerInstance) => {
    const popstateListener = () => picker.close();

    // Store the reference to the listener function in a property or instance variable
    this.onHistoryClosePicker = popstateListener;
  };

  public openFileUpload = () => {
    const processResult = (result: PickerResponse) => {
      if (result.filesUploaded?.length) {
        this.setState({ attachmentForm801: result.filesUploaded[0] });
      }
    };

    const opts: PickerOptions = {
      maxFiles: 1,
      onFileSelected: file => {
        if (file.source === 'url' && !isValidUrl(file.originalPath))
          throw new Error('Invalid URL format. Please try again.');
      },
      onUploadDone: result => processResult(result),
      onOpen: picker => this.onHistoryChange(picker),
    };

    const picker = fileStackClient.picker(opts);
    picker.open();
  };

  public openPreviewModal = () => {
    const { listing } = this.state;
    const { $rootScope, ModalService } = this.props;
    $rootScope.$evalAsync(() => {
      ModalService.openPreviewModal(listing);
    });
  };

  public selectUser = async (user: AgentUserResult) => {
    const {
      $routeParams: { listingId },
      $rootScope,
      ToastService,
    } = this.props;
    try {
      const { offer } = this.state;
      const data = await UserController.searchDetails({
        id: user.id,
        uid: user.uid,
        boardId: user.boardId,
        listingId,
        reason: TrrebApiReason.OfferEntry,
      });
      const coop = {
        ...('id' in data && { id: data.id }),
        ...('uid' in data && { uid: data.uid }),
        board: data.board || '',
        boardId: data.boardId,
        email: data.email || '',
        phone: data.lastPhone || '',
        name: `${data.firstName} ${data.lastName}`,
        org: {
          name: data.org.name || '',
          address: data.org.address,
          phone: data.org.phone || '',
        },
        orgId: data.org.id,
      };
      this.setState({ offer: { ...offer, coop }, coopSelected: true });
      this.validateOfferForm();
    } catch (err) {
      errorReporter.error(`Error searching user details: ${err}`);
      $rootScope.$evalAsync(() => {
        ToastService.add({
          type: 'danger',
          message: `Error searching user details: ${
            err?.response?.data ? err.response.data.message : err
          }`,
        });
      });
    }
  };

  public deselectUser = () => {
    const {
      offer: { coop, ...offerWithoutCoop },
    } = this.state;
    this.setState({ offer: offerWithoutCoop });
  };

  public onDateChange = (date: Moment, value: string) => {
    const { offer, errors, listing } = this.state;
    const dateMoment = moment(date);
    if (!dateMoment.isValid()) {
      return this.setState({
        datePickerValue: value,
        errors: { ...errors, invalidDate: true },
      });
    }
    if (offer?.irrevocable) {
      const oldDate = moment(offer?.irrevocable);
      dateMoment
        .hours(oldDate.hours())
        .minutes(oldDate.minutes())
        .seconds(0)
        .milliseconds(0);
    }
    const offerIrrevocable = offer?.irrevocable
      ? dateMoment.toDate()
      : dateMoment.endOf('day').toDate();

    this.setState({
      datePickerValue: value,
      offer: { ...offer, irrevocable: offerIrrevocable },
      errors: {
        ...errors,
        irrevocable: !offer?.irrevocable,
        pastDate: offerIrrevocable && offerIrrevocable < new Date(),
        beforeCsDate:
          offerIrrevocable &&
          listing!.status === 'Coming Soon' &&
          offerIrrevocable <
            moment(listing!.coming_soon_date!).startOf('day').toDate(),
        invalidDate: false,
      },
    });
  };

  public onTimeChange = (time: Moment, value: string) => {
    const { offer, errors } = this.state;
    const irrevocableDate = moment(offer!.irrevocable);
    const timeDate = moment(time);
    const valid = moment(time).isValid();
    if (!valid) {
      return this.setState({
        timePickerValue: value,
        errors: { ...errors, invalidTime: true },
      });
    }

    const hour = timeDate.hours();
    const min = timeDate.minutes();
    irrevocableDate.hours(hour).minutes(min).seconds(0).milliseconds(0);
    const offerIrrevocable = irrevocableDate.toDate();
    this.setState({
      timePickerValue: value,
      offer: { ...offer, irrevocable: offerIrrevocable },
      errors: {
        ...errors,
        pastDate: offerIrrevocable < new Date(),
        invalidTime: false,
      },
    });
  };

  // Input Form handlers
  public handleCoopForm =
    (key: CoopInputKeys) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const { offer, errors } = this.state;
      const newOffer = { ...offer };
      const newError = { ...errors };
      newOffer.coop[key] = event.target.value;
      newError[coopInputErrorMap[key]] = !newOffer.coop[key];
      this.setState({ offer: newOffer, errors: newError });
    };

  public handleCoopOrgForm =
    (key: CoopOrgInputKeys) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const { offer, errors } = this.state;
      const newOffer = { ...offer };
      const newError = { ...errors };
      newOffer.coop.org[key] = event.target.value;
      newError[coopOrgInputErrorMap[key]] = !newOffer.coop.org[key];
      this.setState({ offer: newOffer, errors: newError });
    };

  public validateOfferForm = () => {
    const { offer, errors } = this.state;
    const newError = { ...errors };
    newError.coopEmail = !offer.coop.email;
    newError.coopPhone = !offer.coop.phone;
    newError.coopName = !offer.coop.name;
    newError.coopOrgName = !offer.coop.org.name;
    newError.coopOrgPhone = !offer.coop.org.phone;
    this.setState({ errors: newError });

    if (offer.agentLookup === 'search') {
      return SEARCH_COOP_ERRORS.reduce((err, key) => {
        if (errors[key]) return false;
        return err;
      }, true);
    }
    return MANUAL_COOP_ERRORS.reduce((err, key) => {
      if (errors[key]) return false;
      return err;
    }, true);
  };

  // Registration Modal handlers
  public openOfferRegistrationModal = () => {
    this.setState({ offerRegistrationModalVisible: true });
  };

  public closeOfferRegistrationModal = () => {
    this.setState({ offerRegistrationModalVisible: false });
  };

  public deleteAttachmentForm = () => {
    this.setState({ attachmentForm801: undefined });
  };

  public onSaveOffer = async (
    agentGroup?: string,
    agentsToNotify?: Coop[],
    messageOverride?: string,
    subjectOverride?: string,
  ) => {
    const { offer } = this.state;
    const newOffer = { ...offer };

    if (agentGroup) newOffer.agentGroup = agentGroup;
    if (agentsToNotify) newOffer.agentsToNotify = agentsToNotify;
    if (messageOverride) newOffer.messageOverride = messageOverride;
    if (subjectOverride) newOffer.subjectOverride = subjectOverride;

    this.setState(
      { offerRegistrationModalVisible: false, offer: newOffer },
      this.saveOffer,
    );
  };

  // Offer Form button handlers
  public submitOfferForm = async () => {
    const { hasListingAccess } = this.state;
    const { isNew } = this.props;
    if (this.validateOfferForm()) {
      if (isNew && hasListingAccess) {
        this.openOfferRegistrationModal();
      } else {
        await this.saveOffer();
      }
    }
  };

  public saveOffer = async () => {
    const {
      $routeParams: { listingId, offerId },
      isNew,
      $rootScope,
      ToastService,
    } = this.props;
    try {
      this.setState({ loading: true });
      const {
        offer,
        attachmentForm801,
        listing,
        offerDetails,
        notificationOptions,
        hasListingAccess,
      } = this.state;
      offer.listingId = listingId;

      if (attachmentForm801?.url) {
        offer.attachmentForm801 = {
          url: attachmentForm801.url,
          filename: attachmentForm801.filename,
        };
      } else {
        delete offer.attachmentForm801;
      }

      const doSubmit = async () => {
        if (!hasListingAccess) {
          const offerBody = {
            orgId: offer.coop.orgId,
            irrevocable: offer.irrevocable,
            listingId: offer.listingId,
            attachmentForm801: offer.attachmentForm801
              ? {
                  url: offer.attachmentForm801.url,
                  filename: offer.attachmentForm801.filename,
                }
              : undefined,
            phone: offer.coop.phone,
          };
          await OfferController.registerOfferForCoop(listingId, offerBody);
        } else {
          await OfferController.registerOffer({ listingId, offerId, offer });
        }

        if (
          listing &&
          !hasListingAccess &&
          ROLES[$rootScope.auth.role] < ROLES.TEAM_ADMIN
        ) {
          await Axios.post(
            `/user/current/listing_notification_setting/${listing.id}`,
            notificationOptions,
          );
        }

        if (isNew && hasListingAccess) {
          await Swal.fire({
            type: 'success',
            title: 'Offer Registered',
            text: 'All relevant parties have been notified based on their preferences.',
            confirmButtonColor: '#62cb31',
            confirmButtonText: 'Done',
          });
        } else {
          await Swal.fire({
            type: 'success',
            title: 'Offer Registered!',
            confirmButtonText: 'OK',
          });
        }
        window.location.replace(
          !hasListingAccess
            ? `#/listing/${listingId}/view`
            : `#/listing/${listingId}/offer`,
        );
      };

      if (!hasListingAccess && offerDetails?.registration?.shouldVerify801) {
        Swal.fire({
          title: 'VALID OFFER DOC',
          text: 'By clicking "Register My Offer", you verify that the file you have attached satisfies the document requirements for offer registration set by your real estate board.',
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Register My Offer',
          cancelButtonText: 'Cancel',
        }).then(async ({ value }) => {
          if (value) {
            await doSubmit();
          } else {
            this.setState({ loading: false });
          }
        });
      } else {
        await doSubmit();
      }
    } catch (err) {
      errorReporter.error(`Error saving offer: ${err}`);
      $rootScope.$evalAsync(() => {
        ToastService.add({
          type: 'danger',
          message: `Error saving offer: ${
            err?.response?.data ? err.response.data.message : err
          }`,
        });
      });
      this.setState({ loading: false });
    }
  };

  public deleteOffer = async () => {
    const { listing = { available: true }, offer } = this.state;
    const {
      $routeParams: { listingId, offerId },
      $rootScope,
      ToastService,
    } = this.props;

    const deleteOfferRequest = async (notifyAgents: boolean) => {
      try {
        this.setState({ loading: true });

        await Axios.post(`/listing/${listingId}/delete_offer/${offerId}`, {
          notifyAgents: notifyAgents.toString(),
        });

        window.location.replace(`#/listing/${listingId}/offer`);
      } catch (err) {
        errorReporter.error(`Error deleting offer: ${err}`);
        $rootScope.$evalAsync(() => {
          ToastService.add({
            type: 'danger',
            message: `Error deleting offer: ${
              err?.response?.data ? err.response.data.message : err
            }`,
          });
        });
        this.setState({ loading: false });
      }
    };

    const { value } = await Swal.fire({
      title: 'Delete Offer',
      text: `Are you sure you want to delete the offer registered by ${offer.coop.name}?`,
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#cb4800',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
    });
    if (value) {
      if (!listing.available) {
        await deleteOfferRequest(false);
        return;
      }

      const { value: notifyAgents } = await Swal.fire({
        title: 'Notify Agents',
        text: 'Would you like to notify agents regarding this change in offer registrations?',
        type: 'info',
        showCancelButton: true,
        confirmButtonColor: '#62cb31',
        confirmButtonText: 'YES, Notify Agents',
        cancelButtonText: 'NO, Do Not Notify',
      });
      await deleteOfferRequest(!!notifyAgents);
    }
  };

  public fetchOfferDetails = async () => {
    try {
      const {
        listing: { id: listingId, line1, line2 },
      } = this.state;
      if (!listingId) return {};

      const {
        data: {
          offerSettings: {
            agentGroup: agentFilter,
            message,
            registration,
            submission,
          },
          offerCount,
        },
      } = await Axios.get<{
        offerSettings: OfferSettingsJsonType;
        offerCount: number;
      }>(`/listing/${listingId}/offer_details`);

      const formattedMessage = replaceTagsWithMarkup(message);

      const defaultSubject = `NEW REGISTERED OFFER (${
        offerCount + 1
      } TOTAL) - ${line1} ${line2 || ''}`;

      return {
        agentFilter,
        offerCount,
        defaultAgentGroup: agentFilter,
        message: formattedMessage,
        defaultMessage: formattedMessage,
        subject: defaultSubject,
        defaultSubject,
        registration,
        submission,
      };
    } catch (err) {
      errorReporter.error(`Error fetching offer details: ${err}`);
      return {};
    }
  };

  public fetchNotificationOptions = async () => {
    const { listing } = this.state;
    if (listing) {
      const { data: notificationOptions } =
        await Axios.get<NotificationOptions>(
          `/user/current/listing_notification_setting/${listing.id}`,
        );
      this.setState({ notificationOptions });
    }
  };

  public onRegisteredOffersNotificationChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.checked;
    const { notificationOptions } = this.state;

    this.setState({
      notificationOptions: { ...notificationOptions, registeredOffers: value },
    });
  };

  public acceptsRegisteredOffer = () => {
    const { offerDetails, hasListingAccess } = this.state;
    return (
      offerDetails?.registration?.isEnabled &&
      (hasListingAccess || offerDetails?.registration?.shouldAllowMls)
    );
  };

  getDateErrorMessage = () => {
    const { errors } = this.state;
    if (errors.irrevocable) return 'Irrevocable date is required.';
    if (errors.pastDate) return 'Irrevocable date must be in the future.';
    if (errors.beforeCsDate)
      return 'Irrevocable date must be after coming soon date.';
    if (errors.invalidDate) return 'Invalid irrevocable date';
    if (errors.invalidTime) return 'Invalid irrevocable time';
  };

  public render() {
    const {
      loading,
      offerRegistrationModalVisible,
      listing,
      offer,
      errors,
      attachmentForm801,
      userType,
      coopSelected,
      timePickerValue,
      datePickerValue,
      offerDetails,
      notificationOptions,
      hasListingAccess,
    } = this.state;
    const {
      isNew,
      $rootScope: { auth },
      $routeParams: { listingId, offerId },
    } = this.props;

    const minDate =
      listing?.status === 'Coming Soon'
        ? moment(listing.coming_soon_date!).startOf('day').toDate()
        : moment().toDate();

    const missingCoopDetails = !offer?.coop?.email || !offer.coop.phone;
    const missingOfferDoc =
      !hasListingAccess &&
      !attachmentForm801?.url &&
      offerDetails?.registration?.shouldSubmitOfferDoc;
    const badIrrevocableDate =
      !offer?.irrevocable ||
      errors.invalidDate ||
      errors.invalidTime ||
      minDate > offer.irrevocable;

    const saveButton = (
      <div key={0} className="pull-right">
        <button
          key={uuidv4()}
          className="btn btn-success pull-right"
          onClick={this.submitOfferForm}
          disabled={missingCoopDetails || missingOfferDoc || badIrrevocableDate}
          data-cy="save-offer"
        >
          {isNew ? 'Register' : 'Update'}
        </button>
        <br />
        <label
          style={{
            fontSize: '12px',
            display: `${
              listing &&
              (hasListingAccess || ROLES[auth.role] >= ROLES.TEAM_ADMIN) &&
              'none'
            }`,
          }}
        >
          <input
            type="checkbox"
            checked={
              notificationOptions && notificationOptions.registeredOffers
            }
            onChange={this.onRegisteredOffersNotificationChange}
          />{' '}
          Notify me regarding registered offers
        </label>
      </div>
    );

    const cancelButton = (
      <button
        key={uuidv4()}
        className="btn btn-default"
        onClick={this.redirectToOffers}
        data-cy="cancel-offer"
      >
        Cancel
      </button>
    );
    const deleteButton = (
      <button
        key={uuidv4()}
        className="btn btn-warning"
        onClick={this.deleteOffer}
        data-cy="delete-offer"
      >
        Delete
      </button>
    );
    const previewButton = (
      <button
        key={uuidv4()}
        onClick={this.openPreviewModal}
        className="btn btn-default hidden-xs"
      >
        <i className="pe-7s-look fat"></i>
      </button>
    );
    const headerTitle = listing && (
      <>
        <a className="listing-link" href={`#/listing/${listing.id}/view`}>{`${
          listing.line1
        } ${listing.line2 || ''}`}</a>
        <span> - {isNew ? 'Register' : 'Update'} Offer</span>
      </>
    );

    const headerButtons = [saveButton];
    if (hasListingAccess) {
      headerButtons.push(cancelButton);
    }
    if (!isNew && !offer.isDeleted) headerButtons.unshift(deleteButton);
    if (
      listing &&
      !listing.isDeleted &&
      listing.org.features.indexOf('APPOINTMENT') !== -1 &&
      listing.org.id === auth.org.id &&
      auth.isAdmin
    ) {
      headerButtons.unshift(previewButton);
    }

    if (listing?.visibility === 'pending') {
      return (
        <PanelBody fullHeight>
          <StyledErrorContainer>
            <h3>You do not have permissions to view this page.</h3>
          </StyledErrorContainer>
        </PanelBody>
      );
    }

    const { canRegisterOffer } = this.state;
    if (!canRegisterOffer) {
      return (
        <PanelBody fullHeight>
          <StyledErrorContainer>
            <h3></h3>
            <h3>You do not have permissions to this listing.</h3>
          </StyledErrorContainer>
        </PanelBody>
      );
    }

    if (loading) return <Loading size={'fa-3x'} />;
    if (!this.acceptsRegisteredOffer()) {
      return (
        <div className="col-md-6">
          <OfferDisabledRegistration listing={listing} />
        </div>
      );
    }

    return (
      <>
        <OfferRegistrationModal
          closeModal={this.closeOfferRegistrationModal}
          onSaveOffer={this.onSaveOffer}
          visible={offerRegistrationModalVisible}
          listing={listing}
          userType={userType}
          details={offerDetails}
        />
        <PanelHeader title={headerTitle} extra={headerButtons} />
        <PanelBody fullHeight={false}>
          {ROLES[auth.role] >= ROLES.TEAM_ADMIN || hasListingAccess ? (
            <div style={{ width: '100%' }}>
              {isNew && (
                <div className="row" style={{ marginBottom: '15px' }}>
                  <div className="col-md-12">
                    <ul className="nav nav-tabs">
                      <li
                        className={
                          offer.agentLookup === 'search' ? 'active' : ''
                        }
                      >
                        <a onClick={() => this.selectAgentLookup('search')}>
                          Search
                        </a>
                      </li>
                      <li
                        className={
                          offer.agentLookup === 'manual' ? 'active' : ''
                        }
                        style={{
                          display: !hasListingAccess ? 'none' : 'block',
                        }}
                      >
                        <a
                          onClick={() => this.selectAgentLookup('manual')}
                          data-cy="offer-coop-manual"
                        >
                          Manual
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              )}

              <div className="row">
                <div className="col-md-4">
                  {offer.agentLookup === 'search' && (
                    <div>
                      {isNew && (
                        <div>
                          <label>Agent Name:</label>
                          <AgentRosterInput
                            selectUser={this.selectUser}
                            deselectUser={this.deselectUser}
                            auth={auth}
                            listingId={listing.id}
                          />
                        </div>
                      )}

                      {offer.coop && (coopSelected || !isNew) && (
                        <div>
                          <div className="form-group">
                            {offer.coop.name}
                            <h5>{offer.coop.org.name}</h5>
                            {offer.coop.org.address}
                            <br />
                            Tel: {formatPhoneNumber(offer.coop.org.phone)}
                          </div>

                          <div className="form-group">
                            <label>Email</label>
                            <input
                              type="text"
                              className="form-control"
                              value={offer.coop.email}
                              onChange={this.handleCoopForm('email')}
                              placeholder="Email"
                              data-cy="offer-coop-email"
                              disabled={offer.isDeleted}
                            />
                            {errors.coopEmail && (
                              <label className="error">
                                Email is required.
                              </label>
                            )}
                          </div>

                          <div className="form-group">
                            <label>Mobile</label>
                            <input
                              type="text"
                              className="form-control"
                              value={offer.coop.phone}
                              onChange={this.handleCoopForm('phone')}
                              placeholder="Mobile"
                              data-cy="offer-coop-mobile"
                              disabled={offer.isDeleted}
                              ref={this.phoneRef}
                            />
                            {errors.coopPhone && (
                              <label className="error">
                                Phone is required.
                              </label>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {offer.agentLookup === 'manual' && (
                    <div>
                      <div className="form-group">
                        <label>Agent Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={offer.coop.name}
                          onChange={this.handleCoopForm('name')}
                          placeholder="Agent Name"
                          disabled={!isNew}
                          data-cy="offer-manual-coop-name"
                        />
                        {errors.coopName && (
                          <label className="error">
                            Agent name is required.
                          </label>
                        )}
                      </div>

                      <div className="form-group">
                        <label>Showing Agent Brokerage</label>
                        <input
                          type="text"
                          className="form-control"
                          value={offer.coop.org.name}
                          onChange={this.handleCoopOrgForm('name')}
                          placeholder="Brokerage Name"
                          disabled={!isNew}
                          data-cy="offer-manual-coop-org-name"
                        />
                        {errors.coopOrgName && (
                          <label className="error">
                            Brokerage name is required.
                          </label>
                        )}
                      </div>

                      <div className="form-group">
                        <label>Brokerage Telephone</label>
                        <input
                          type="text"
                          className="form-control"
                          value={offer.coop.org.phone}
                          onChange={this.handleCoopOrgForm('phone')}
                          placeholder="Office Phone #"
                          disabled={!isNew}
                          data-cy="offer-manual-coop-org-phone"
                          ref={this.companyPhoneRef}
                        />
                        {errors.coopOrgPhone && (
                          <label className="error">
                            Brokerage telephone is required.
                          </label>
                        )}
                      </div>

                      <div className="form-group">
                        <label>Email</label>
                        <input
                          type="text"
                          className="form-control"
                          value={offer.coop.email}
                          onChange={this.handleCoopForm('email')}
                          placeholder="Email"
                          data-cy="offer-manual-coop-email"
                        />
                        {errors.coopEmail && (
                          <label className="error">Email is required.</label>
                        )}
                      </div>

                      <div className="form-group">
                        <label>Phone</label>
                        <input
                          type="text"
                          className="form-control"
                          value={offer.coop.phone}
                          onChange={this.handleCoopForm('phone')}
                          placeholder="Mobile"
                          data-cy="offer-manual-coop-phone"
                          ref={this.phoneRef}
                        />
                        {errors.coopPhone && (
                          <label className="error">Phone is required.</label>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="col-md-4 col-md-offset-1">
                  <div
                    style={{
                      display:
                        hasListingAccess ||
                        offerDetails?.registration?.shouldSubmitOfferDoc
                          ? 'block'
                          : 'none',
                    }}
                  >
                    <button
                      className="btn btn-primary"
                      onClick={this.openFileUpload}
                    >
                      ATTACH OFFER DOC
                    </button>
                    {attachmentForm801?.url ? (
                      <div>
                        <h4>Attachments</h4>
                        <a
                          href={attachmentForm801.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {attachmentForm801.filename}
                        </a>
                        <i
                          className="pe-7s-trash fat pull-right"
                          onClick={this.deleteAttachmentForm}
                        ></i>
                      </div>
                    ) : (
                      <>
                        <h4>No form attached.</h4>
                        {!hasListingAccess &&
                          offerDetails?.registration?.shouldSubmitOfferDoc && (
                            <label className="error">
                              Offer Doc is required.
                            </label>
                          )}
                      </>
                    )}
                  </div>
                  <br />
                  <h4>Irrevocability</h4>
                  <div className="form-horizontal">
                    <div className="form-group">
                      <label
                        className="col-sm-3 control-label"
                        htmlFor="irrevocableDate"
                      >
                        Date:
                      </label>
                      <div className="col-sm-9">
                        <div className="date-time-container">
                          <div>
                            <DatePicker
                              inputValue={datePickerValue}
                              value={offer.irrevocable}
                              onChange={this.onDateChange}
                              emptyLabel="Select/Enter Date"
                              format="YYYY-MM-DD"
                              variant="inline"
                              inputVariant="outlined"
                              autoOk
                              disablePast
                              {...(listing?.status === 'Coming Soon' && {
                                minDate: new Date(listing.coming_soon_date!),
                              })}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    {offer.irrevocable && (
                      <div className="form-group">
                        <label
                          className="col-sm-3 control-label"
                          htmlFor="irrevocableTime"
                        >
                          Time:
                        </label>
                        <div className="col-sm-9">
                          <div className="date-time-container">
                            <div>
                              <TimePicker
                                inputValue={timePickerValue}
                                value={offer.irrevocable}
                                onChange={this.onTimeChange}
                                minutesStep={10}
                                variant="inline"
                                inputVariant="outlined"
                                mask="__:__ _M"
                                autoOk
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {this.getDateErrorMessage() && (
                      <label className="error">
                        {this.getDateErrorMessage()}
                      </label>
                    )}
                  </div>
                </div>
              </div>
              {!isNew && (
                <div style={{ marginTop: '2em' }}>
                  <ActionTabs
                    type="offer"
                    offerId={offer.id || offerId}
                    listingId={listingId}
                    timezone={listing.timezone}
                  />
                </div>
              )}
            </div>
          ) : (
            <div style={{ width: '100%', marginBottom: '20px' }}>
              <AgentOfferEdit
                user={auth}
                listing={listing}
                offerDetails={offerDetails}
                offer={offer}
                onTimeChange={this.onTimeChange}
                onDateChange={this.onDateChange}
                selectUser={this.selectUser}
                handleCoopForm={this.handleCoopForm}
                openFileUpload={this.openFileUpload}
                attachmentForm801={attachmentForm801}
                deleteAttachmentForm={this.deleteAttachmentForm}
                hasListingAccess={hasListingAccess}
                getDateErrorMessage={this.getDateErrorMessage}
              />
            </div>
          )}
        </PanelBody>
      </>
    );
  }
}

export default OfferEdit;

const StyledErrorContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;
